'use no memo'

import { useMemo } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import type { ProjectTabValue } from '@/routes/features/projects/types'
import { useChildProjects, useRootProjects } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import { Loading } from '@/components/app/indicators/Loading'
import { ProjectSavedViewShell } from '@/routes/features/projects/components/ProjectSavedViewShell'
import { useViewHydrator } from '@/routes/features/views/useViewHydrator'

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/spaces/$spaceKey/projects/$projectKey/views/$viewId',
)({
  component: SpaceProjectViewPage,
})

function SpaceProjectViewPage() {
  const { workspaceSlug, spaceKey, projectKey, viewId } = Route.useParams()
  const navigate = useNavigate()
  const workspaceId = useLoginStore((s) => s.workspaceId)

  const { projects: rootProjects, isLoading: spacesLoading } = useRootProjects()
  const space = useMemo<ProjectDocument | null>(() => {
    return (
      rootProjects.find((p) => {
        if (p.isDeleted) return false
        if (p.projectKey.toLowerCase() !== spaceKey.toLowerCase()) return false
        if (workspaceId && p.envId !== workspaceId) return false
        return true
      }) ?? null
    )
  }, [rootProjects, spaceKey, workspaceId])

  const { projects: childProjects } = useChildProjects(space?.id)
  const project = useMemo<ProjectDocument | null>(() => {
    return (
      childProjects?.find((p) => {
        if (p.isDeleted) return false
        if (p.projectKey.toLowerCase() !== projectKey.toLowerCase())
          return false
        return true
      }) ?? null
    )
  }, [childProjects, projectKey])

  const { status, view } = useViewHydrator({
    viewId,
    scopeEntityId: project?.id,
    scope: 'project',
  })

  const handleTabChange = (tab: ProjectTabValue) => {
    if (tab === 'views') {
      navigate({
        to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey/views',
        params: { workspaceSlug, spaceKey, projectKey },
      })
      return
    }
    navigate({
      to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey',
      params: { workspaceSlug, spaceKey, projectKey },
      search: tab === 'tasks' ? {} : { projectTab: tab },
    })
  }

  if (spacesLoading || status === 'pending') {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center">
        <Loading />
      </div>
    )
  }

  if (!space || !project) return null

  return (
    <ProjectSavedViewShell
      projectId={project.id}
      status={status}
      view={view}
      onTabChange={handleTabChange}
      onBackToViews={() =>
        navigate({
          to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey/views',
          params: { workspaceSlug, spaceKey, projectKey },
        })
      }
    />
  )
}
